import React, {
  useState,
  useEffect
} from 'react';
import { observer } from "mobx-react"

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link as RouterLink
} from "react-router-dom";

import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar,
  Avatar,
  Toolbar,
  Drawer,
  Typography,
  Button,
  Grid,
  Paper,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  Hidden,
  Slide,
  Collapse,
  Zoom,
  useScrollTrigger,
  ThemeProvider,
  useMediaQuery,
  Divider,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import LanguageIcon from '@material-ui/icons/Language';
import MoreIcon from '@material-ui/icons/MoreVert';
import MenuIcon from '@material-ui/icons/Menu';
import TranslateIcon from '@material-ui/icons/Translate';

import { spring, AnimatedSwitch } from 'react-router-transition';

import {
  SiteLoading,
  Login,
  IssueReportPanel,
  UserProfile,
  GlobalLoadingIndicator,
  HomePageInfo,
  ThesisDefensePageInfo,
  ThesisWritingPageInfo,
  ThesisProposalPageInfo,
  ThesisUGBPageInfo,
  ThesisFirstSeminarPageInfo,
  ThesisSecondSeminarPageInfo
} from './views';

import HomePage from './pages/HomePage';
import ThesisUGBPage from './pages/ThesisUGBPage';
import ThesisProposalPage from './pages/ThesisProposalPage';
import ThesisWritingPage from './pages/ThesisWritingPage';
import ThesisFirstSeminarPage from './pages/ThesisFirstSeminarPage';
import ThesisSecondSeminarPage from './pages/ThesisSecondSeminarPage';
import ThesisDefensePage from './pages/ThesisDefensePage';

import { useLanguage } from './data/Lang';
import { useSession } from './data/Session';
import { useStatus } from './data/Status';

import theme from './theme';
import localization from './utils/localizations';

import {
  BaseAPI,
} from './data/BaseAPI';

import logo from './img/logo.png';
import './App.scss';

const appConfig = (window as any).APP_CONFIG;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#fafafa",
    display: 'flex',
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  appTitle: {
    flexGrow: 1,
    color: '#ffffff'
  },
  appSubTitle: {
    flexGrow: 1,
    color: '#ffffff',
    fontWeight: 300,
    fontSize: '1rem'
  },
  mainBody: {
    position: 'relative',
    overflow: 'hidden',
    padding: 10,
    flexGrow: 1,
    minHeight: '720px',
  },
  headerContainer: {
    backgroundColor: '#2c558d',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10
  },
  siteContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    alignSelf: 'flex-end',
  },
  headerLogo: {
    display: 'block',
  },
  headerTextContainer: {
    marginLeft: 20,
  },
  sidebarPanelContainer: {
    marginBottom: 20,
  },
  footer: {
    marginTop: 20,
    textAlign: 'center'
  },
  pageNotFoundContainer: {
    padding: 10,
  },
  pageNotFoundTitle: {
    marginBottom: 20
  },
  betaLabel: {
    fontSize: '0.75rem'
  },
  alert: {
    marginTop: 20,
    marginBottom: 20,
  },
  topMenuMainContainer: {
    display: 'flex',
  },
  topMenuButtonContainer: {
    paddingTop: 10,
  },
  topMenuButton: {
    color: '#ffffff'
  },
  languageSelectorLoginContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  appbar: {
    zIndex: theme.zIndex.drawer + 2,
  },
  toolbar: {
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbarTitle: {
    flexGrow: 1,
    alignSelf: 'flex-end',
  },
  drawer: {
    width: 300,
    flexShrink: 0,
  },
  drawerPaper: {
    width: 300,
  },
  drawerContainer: {
    overflow: 'auto',
    padding: 20,
  },
  mainContentContainer: {
    position: 'relative',
  },
  bgPrimaryColor: {
    backgroundColor: theme.palette.primary.main,
  }
}));

function bounce(val: any) {
  return spring(val, {
    stiffness: 330,
    damping: 35,
  });
}

function ScrollTriggeredSlide(props: {children: any}) {
  const scrollTrigger = useScrollTrigger();
  return (<Slide appear={false} direction="down" in={!scrollTrigger}>{props.children}</Slide>);
}

function ScrollTriggeredCollapse(props: {children: any}) {
  const scrollTrigger = useScrollTrigger();
  return (<Collapse appear={false} in={!scrollTrigger}>{props.children}</Collapse>);
}

function App() {
  const isMobile = useMediaQuery('(max-width:600px)');
  // child matches will...
  const bounceTransition = {
    // start in a transparent, upscaled state
    atEnter: {
      opacity: 0,
      scale: 100,
    },
    // leave in a transparent, downscaled state
    atLeave: {
      opacity: bounce(0),
      scale: bounce(-100),
    },
    // and rest at an opaque, normally-scaled state
    atActive: {
      opacity: bounce(1),
      scale: bounce(0),
    },
  };

  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [langMenuAnchorEl, setLangMenuAnchorEl] = React.useState(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState(null);
  const [session, sessionLoading, loadSession] = useSession();
  const [status, statusLoading, loadStatus] = useStatus();
  const [language, setLanguage] = useLanguage();
  
  if (language !== localization.getLanguage()) {
    localization.setLanguage(language);
  }

  useEffect(() => {
    let intervalHandle = setInterval(() => {
      if (session.success) loadSession();
    }, 60000);
    return () => {
      clearInterval(intervalHandle);
    }
  }, [])

  const onLogout = async () => {
    const api = new BaseAPI();
    try {
      const [result, response, error] = await api.post({}, 'logout/');
    } catch (error) {
      console.error(error);
    }

    await loadSession();
    await loadStatus();
  }

  if (!session.success && (sessionLoading || statusLoading)) {
    return (
      <SiteLoading />
    );
  }

  if (!session.success) {
    return (
      <Login
        languageSelectorEl={
          <div className={classes.languageSelectorLoginContainer}>
            <Button
              onClick={(event: any) => setLangMenuAnchorEl(event.currentTarget)}
              size="small"
              startIcon={<LanguageIcon />}
            >
              {language == 'en-us' ? 'English' : 'Bahasa Indonesia'}
            </Button>
            <Menu
              id="language-selector"
              anchorEl={langMenuAnchorEl}
              keepMounted
              open={Boolean(langMenuAnchorEl)}
              onClose={() => setLangMenuAnchorEl(null)}
            >
              <MenuItem
                selected={language == 'id'}
                onClick={() => {
                  setLanguage('id');
                  setLangMenuAnchorEl(null);
                }}>Bahasa Indonesia</MenuItem>
              <MenuItem
                selected={language == 'en-us'}
                onClick={() => {
                  setLanguage('en-us');
                  setLangMenuAnchorEl(null);
                }}>English</MenuItem>
            </Menu>
          </div>
        }
        onLogin={async () => {
          await loadSession();
          await loadStatus();
        }}
      />
    );
  }

  const topBarButtons = (
    <>
      <Button
        aria-label="Language"
        color="inherit"
        startIcon={<LanguageIcon />}
        onClick={(event: any) => setLangMenuAnchorEl(event.currentTarget)}
      >
        {language == 'en-us' ? 'English' : 'Indonesia'}
      </Button>
      <Menu
        id="language-selector"
        anchorEl={langMenuAnchorEl}
        keepMounted
        open={Boolean(langMenuAnchorEl)}
        onClose={() => setLangMenuAnchorEl(null)}
      >
        <MenuItem
          selected={language == 'id'}
          onClick={() => {
            setLanguage('id');
            setLangMenuAnchorEl(null);
          }}>Indonesia</MenuItem>
        <MenuItem
          selected={language == 'en-us'}
          onClick={() => {
            setLanguage('en-us');
            setLangMenuAnchorEl(null);
          }}>English</MenuItem>
      </Menu>
      <IconButton
        aria-label="display more actions"
        edge="end"
        color="inherit"
        onClick={(event: any) => setMoreMenuAnchorEl(event.currentTarget)}
      >
        <Avatar src={session?.student?.avatar} />
      </IconButton>
      <Menu
        id="main-menu-more"
        anchorEl={moreMenuAnchorEl}
        keepMounted
        open={Boolean(moreMenuAnchorEl)}
        onClose={() => setMoreMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMobileOpen(true);
            setMoreMenuAnchorEl(null);
          }}
        >{session?.student?.full_name}</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMoreMenuAnchorEl(null);
            onLogout();
          }}
        >Logout</MenuItem>
      </Menu>
    </>
  );

  return (
    <Router>
      <div className={classes.root}>
        <ScrollTriggeredSlide>
          <AppBar position="fixed" className={classes.appbar}>
            <Toolbar className={classes.toolbar}>
              <div className={classes.siteContainer}>
                <RouterLink to={`${appConfig.homepage}`}><img src={logo} width="60" height="60" alt="" className={classes.headerLogo} /></RouterLink>
                <div className={classes.headerTextContainer}>
                  <Typography variant="h5" className={classes.appTitle}>{localization.appTitle}</Typography>
                  <Hidden smDown>
                    <Typography variant="h6" className={classes.appSubTitle}>{localization.appSubTitle}</Typography>
                  </Hidden>
                </div>
              </div>
              <Hidden smDown>
                {topBarButtons}
              </Hidden>
            </Toolbar>
          </AppBar>
        </ScrollTriggeredSlide>

        <Drawer
          className={classes.drawer}
          open={mobileOpen}
          variant={isMobile ? "temporary" : "permanent"}
          classes={{
            paper: classes.drawerPaper,
          }}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <div className={classes.drawerContainer}>
            {!isMobile &&
              <ScrollTriggeredCollapse>
                <div style={{height: '88px', width: '100%'}}></div>
                </ScrollTriggeredCollapse>
            }
            <div className={classes.sidebarPanelContainer}>
              <UserProfile />
            </div>

            <Switch>
              <Route exact path={`${appConfig.homepage}s2/ugb/`}>
                <ThesisUGBPageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/proposal/`}>
                <ThesisProposalPageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/tesis/`}>
                <ThesisWritingPageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/seminar-1/`}>
                <ThesisFirstSeminarPageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/seminar-2/`}>
                <ThesisSecondSeminarPageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/sidang/`}>
                <ThesisDefensePageInfo />
              </Route>
              <Route exact path={`${appConfig.homepage}`}>
                <HomePageInfo />
              </Route>
              <Route>
              </Route>
            </Switch>

            <Paper className={classes.footer}>
              <Button color={language == 'id' ? 'primary' : 'default'} onClick={() => {
                setLanguage('id');
              }}>ID</Button> |
              <Button color={language == 'en-us' ? 'primary' : 'default'} onClick={() => {
                setLanguage('en-us');
              }}>EN</Button>
            </Paper>
          </div>
        </Drawer>

        <div className={classes.mainBody}>
        <Toolbar className={classes.toolbar} />

        <Hidden mdUp>
          <Toolbar className={classes.toolbar}>
            <IconButton
              edge="start"
              className={classes.menuButton}
              color="inherit"
              aria-label="open drawer"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Typography className={classes.toolbarTitle} variant="h5" noWrap>
              
            </Typography>
            {topBarButtons}
          </Toolbar>
        </Hidden>

        <Grid container>
          <Grid item xs={12} className={classes.mainContentContainer}>
            <AnimatedSwitch
              atEnter={bounceTransition.atEnter}
              atLeave={bounceTransition.atLeave}
              atActive={bounceTransition.atActive}
              mapStyles={(styles: any) => {
                const stylesheet = { position: 'relative', opacity: styles.opacity, transform: 'none', height: 'auto' };
                if ( (styles.scale !== 0)) {
                  stylesheet.transform = `translateX(${styles.scale}%)`;
                }
                if ( (styles.scale < 0)) {
                  stylesheet.position = 'absolute';
                }
                return stylesheet;
              }}
              className="switch-wrapper"
            >
              <Route exact path={`${appConfig.homepage}s2/ugb/`}>
                <ThesisUGBPage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/proposal/`}>
                <ThesisProposalPage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/tesis/`}>
                <ThesisWritingPage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/seminar-1/`}>
                <ThesisFirstSeminarPage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/seminar-2/`}>
                <ThesisSecondSeminarPage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2/sidang/`}>
                <ThesisDefensePage />
              </Route>
              <Route exact path={`${appConfig.homepage}s2`}>
                <HomePage />
              </Route>
              <Route exact path={`${appConfig.homepage}`}>
                <HomePage />
              </Route>
              <Route>
                <Paper className={classes.pageNotFoundContainer}>
                  <Typography variant="h5" className={classes.pageNotFoundTitle}>404 Page Not Found</Typography>
                  <Button variant="contained" color="primary" to={`${appConfig.homepage}`} component={RouterLink}>Return to Home Page</Button>
                </Paper>
                <IssueReportPanel />
              </Route>
            </AnimatedSwitch>
          </Grid>
        </Grid>
        </div>
      </div>
      <GlobalLoadingIndicator />
    </Router>
  );
}

export default observer(App);
